import { create } from "zustand";
import {
  applyPropertyEdit,
  areValuesEqual,
  clearItemTreeChanges,
  cloneCondition,
  getItemAtPath,
} from "../../utils/conditions";

type BulkEditTarget =
  | {
      type: "condition";
      field: "name";
    }
  | {
      type: "attribute";
      path: string;
      propertyName: string;
    }
  | {
      type: "parameter";
      path: string;
      processPath: number[];
      propertyName: string;
    };

export type BulkCellChange = {
  key: string;
  conditionId: number;
  conditionName: string;
  field: string;
  columnHeader: string;
  target: BulkEditTarget;
  previousValue: ValueType | null;
  currentValue: ValueType | null;
  previousDisplay: string;
  currentDisplay: string;
};

type TrackBulkCellChangeInput = Omit<BulkCellChange, "key">;

export type BulkConditionDiff = {
  conditionId: number;
  conditionName: string;
  changes: BulkCellChange[];
};

export type GridViewSavedFilters = {
  visibleColumns: string[];
  visibleConditions: number[];
};

type GridViewFilterState = {
  filtersByExpId: Record<string, GridViewSavedFilters>;
  setFilters: (
    expId: string,
    visibleColumns: Set<string>,
    visibleConditions: Set<number>
  ) => void;
  clearFilters: (expId: string) => void;
};

interface BulkEditState {
  baselineByConditionId: Record<number, FormspecCondition>;
  cellChangesByKey: Record<string, BulkCellChange>;
  pendingSaveConditionIds: number[];
  bulkSaveTaskId: string | null;
  initializeBaseline: (conditions: FormspecCondition[]) => void;
  upsertBaseline: (conditions: FormspecCondition[]) => void;
  trackCellChange: (change: TrackBulkCellChangeInput) => void;
  clearChanges: () => void;
  clearChangesForConditionIds: (conditionIds: number[]) => void;
  startPendingSave: (taskId: string, conditionIds: number[]) => void;
  finishPendingSave: (conditionIds?: number[]) => void;
  clearPendingSave: () => void;
  getDiffSummary: () => BulkConditionDiff[];
  buildUpdatedConditionsForSave: () => FormspecCondition[];
}

/**
 * Apply a single bulk cell edit to an already-cloned condition tree. Defers
 * to the shared `applyPropertyEdit` helper, which handles both attribute and
 * (possibly nested) parameter targets.
 *
 * `condition` must be caller-owned (e.g., produced by `cloneCondition`); it
 * is mutated in place.
 */
const applyCellChangeToCondition = (
  condition: FormspecCondition,
  change: BulkCellChange
) => {
  if (change.target.type === "condition") {
    if (change.target.field === "name") {
      condition.name = String(change.currentValue ?? "");
    }
    return;
  }

  const targetItem = getItemAtPath(condition, change.target.path);
  if (!targetItem) {
    return;
  }

  applyPropertyEdit(
    targetItem,
    change.target,
    change.previousValue,
    change.currentValue
  );
};

const buildCellKey = (conditionId: number, field: string): string => {
  return `${conditionId}::${field}`;
};

export const buildDefaultGridViewVisibleColumns = (
  propertyNames: string[]
): Set<string> => {
  return new Set([
    "dragHandle",
    "conditionId",
    "conditionName",
    ...propertyNames,
  ]);
};

export const reconcileGridViewFilters = ({
  savedFilters,
  allPropertyNames,
  allConditionIds,
}: {
  savedFilters?: GridViewSavedFilters;
  allPropertyNames: string[];
  allConditionIds: Set<number>;
}): { visibleColumns: Set<string>; visibleConditions: Set<number> } => {
  const defaultVisibleColumns =
    buildDefaultGridViewVisibleColumns(allPropertyNames);

  if (!savedFilters) {
    return {
      visibleColumns: defaultVisibleColumns,
      visibleConditions: new Set(allConditionIds),
    };
  }

  const validPropertyNames = new Set(allPropertyNames);
  const matchedPropertyColumns = savedFilters.visibleColumns.filter((field) =>
    validPropertyNames.has(field)
  );
  const visibleColumns = new Set<string>([
    "dragHandle",
    "conditionId",
    "conditionName",
    ...matchedPropertyColumns,
  ]);
  const reconciledVisibleColumns =
    savedFilters.visibleColumns.length > 0 && matchedPropertyColumns.length === 0
      ? defaultVisibleColumns
      : visibleColumns;

  const visibleConditions = new Set<number>();
  savedFilters.visibleConditions.forEach((conditionId) => {
    if (allConditionIds.has(conditionId)) {
      visibleConditions.add(conditionId);
    }
  });

  return {
    visibleColumns: reconciledVisibleColumns,
    visibleConditions:
      savedFilters.visibleConditions.length > 0 && visibleConditions.size === 0
        ? new Set(allConditionIds)
        : visibleConditions,
  };
};

export const useGridViewFilterStateStore = create<GridViewFilterState>(
  (set) => ({
    filtersByExpId: {},

    setFilters: (expId, visibleColumns, visibleConditions) => {
      set((state) => ({
        filtersByExpId: {
          ...state.filtersByExpId,
          [expId]: {
            visibleColumns: Array.from(visibleColumns),
            visibleConditions: Array.from(visibleConditions),
          },
        },
      }));
    },

    clearFilters: (expId) => {
      set((state) => {
        if (!state.filtersByExpId[expId]) {
          return state;
        }

        const nextFilters = { ...state.filtersByExpId };
        delete nextFilters[expId];
        return { filtersByExpId: nextFilters };
      });
    },
  })
);

/**
 * Private helper applied via zustand `set`. Computes the next baseline map
 * for the given mode without mutating store state directly.
 */
const computeNextBaseline = (
  current: Record<number, FormspecCondition>,
  conditions: FormspecCondition[],
  mode: "initialize" | "upsert"
): Record<number, FormspecCondition> => {
  const next = { ...current };
  conditions.forEach((condition) => {
    if (condition.cell_condition_id == null) {
      return;
    }

    const conditionId = condition.cell_condition_id;
    if (mode === "initialize") {
      if (!next[conditionId]) {
        next[conditionId] = cloneCondition(condition);
      }
      return;
    }

    next[conditionId] = cloneCondition(condition);
  });
  return next;
};

export const useBulkEditStateStore = create<BulkEditState>((set, get) => ({
  baselineByConditionId: {},
  cellChangesByKey: {},
  pendingSaveConditionIds: [],
  bulkSaveTaskId: null,

  /**
   * Preserves existing baseline entries and only seeds missing IDs.
   */
  initializeBaseline: (conditions) => {
    set((state) => {
      if (!conditions || conditions.length === 0) {
        return state;
      }
      return {
        baselineByConditionId: computeNextBaseline(
          state.baselineByConditionId,
          conditions,
          "initialize"
        ),
      };
    });
  },

  /**
   * Inserts or updates baseline entries for the provided condition IDs.
   */
  upsertBaseline: (conditions) => {
    set((state) => {
      if (!conditions || conditions.length === 0) {
        return state;
      }
      return {
        baselineByConditionId: computeNextBaseline(
          state.baselineByConditionId,
          conditions,
          "upsert"
        ),
      };
    });
  },

  trackCellChange: (change) => {
    set((state) => {
      const key = buildCellKey(change.conditionId, change.field);

      if (areValuesEqual(change.previousValue, change.currentValue)) {
        if (!state.cellChangesByKey[key]) {
          return state;
        }

        const nextChanges = { ...state.cellChangesByKey };
        delete nextChanges[key];
        return { cellChangesByKey: nextChanges };
      }

      const nextChanges = {
        ...state.cellChangesByKey,
        [key]: {
          ...change,
          key,
        },
      };

      return {
        cellChangesByKey: nextChanges,
      };
    });
  },

  clearChanges: () => {
    set({ cellChangesByKey: {} });
  },

  clearChangesForConditionIds: (conditionIds) => {
    set((state) => {
      if (!conditionIds || conditionIds.length === 0) {
        return state;
      }

      const ids = new Set(conditionIds);
      const nextChanges = { ...state.cellChangesByKey };

      Object.entries(nextChanges).forEach(([key, change]) => {
        if (ids.has(change.conditionId)) {
          delete nextChanges[key];
        }
      });

      return {
        cellChangesByKey: nextChanges,
      };
    });
  },

  startPendingSave: (taskId, conditionIds) => {
    set({
      bulkSaveTaskId: taskId,
      pendingSaveConditionIds: Array.from(new Set(conditionIds)),
    });
  },

  finishPendingSave: (conditionIds) => {
    set((state) => {
      if (!conditionIds) {
        return {
          bulkSaveTaskId: null,
          pendingSaveConditionIds: [],
        };
      }

      const finishedIds = new Set(conditionIds);
      const pendingSaveConditionIds = state.pendingSaveConditionIds.filter(
        (conditionId) => !finishedIds.has(conditionId)
      );

      return {
        bulkSaveTaskId:
          pendingSaveConditionIds.length > 0 ? state.bulkSaveTaskId : null,
        pendingSaveConditionIds,
      };
    });
  },

  clearPendingSave: () => {
    set({ bulkSaveTaskId: null, pendingSaveConditionIds: [] });
  },

  getDiffSummary: () => {
    const changes = Object.values(get().cellChangesByKey);
    const byCondition = new Map<number, BulkConditionDiff>();

    changes.forEach((change) => {
      if (!byCondition.has(change.conditionId)) {
        byCondition.set(change.conditionId, {
          conditionId: change.conditionId,
          conditionName: change.conditionName,
          changes: [],
        });
      }

      byCondition.get(change.conditionId)!.changes.push(change);
    });

    return Array.from(byCondition.values())
      .map((group) => ({
        ...group,
        changes: group.changes.sort((a, b) =>
          a.columnHeader.localeCompare(b.columnHeader)
        ),
      }))
      .sort((a, b) => a.conditionId - b.conditionId);
  },

  buildUpdatedConditionsForSave: () => {
    const { baselineByConditionId, cellChangesByKey } = get();
    const grouped = new Map<number, BulkCellChange[]>();

    Object.values(cellChangesByKey).forEach((change) => {
      const existing = grouped.get(change.conditionId) || [];
      existing.push(change);
      grouped.set(change.conditionId, existing);
    });

    const updatedConditions: FormspecCondition[] = [];

    grouped.forEach((changes, conditionId) => {
      const baseline = baselineByConditionId[conditionId];
      if (!baseline) {
        return;
      }

      const updated = clearItemTreeChanges(
        cloneCondition(baseline)
      ) as FormspecCondition;
      changes.forEach((change) => applyCellChangeToCondition(updated, change));
      updatedConditions.push(updated);
    });

    return updatedConditions;
  },
}));
